import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Product, ProductType } from "@/core/entities/product";

interface ProductViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product | null;
}

export function ProductViewDialog({
  open,
  onOpenChange,
  product,
}: Readonly<ProductViewDialogProps>) {
  const getBuyOrSellBadge = (buyOrSell: ProductType) => {
    switch (buyOrSell) {
      case ProductType.SELL:
        return (
          <Badge className="bg-muted text-secondary-foreground hover:bg-muted">
            Sell
          </Badge>
        );
      case ProductType.BUY:
        return (
          <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary">
            Buy
          </Badge>
        );
      case ProductType.BOTH:
        return (
          <Badge className="bg-primary/10 text-primary hover:bg-primary/10">
            Both
          </Badge>
        );
      default:
        return null;
    }
  };

  if (!product) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full md:max-w-4xl max-w-full overflow-y-auto p-4 sm:p-8 md:p-12"
      >
        <SheetHeader className="p-0">
          <SheetTitle>Product Details</SheetTitle>
          <SheetDescription>
            Comprehensive view of the product information and pricing tiers
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4">
          {/* Basic Information */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="pb-2">Product Name</Label>
              <p className="text-sm text-muted-foreground mt-1">
                {product.name}
              </p>
            </div>

            <div>
              <Label className="pb-2">Product Code</Label>
              <p className="text-sm text-muted-foreground mt-1">
                {product.productCode || <span className="italic">Not set</span>}
              </p>
            </div>

            <div>
              <Label className="pb-2">Category</Label>
              <p className="text-sm text-muted-foreground mt-1">
                {product.category}
              </p>
            </div>

            <div className="col-span-1 sm:col-span-2  w-fit h-fit overflow-hidden">
              <Label className="pb-2">Logo</Label>
              <div className="max-h-[30dvh] h-full w-fit">
                <img
                  src={product.logo}
                  alt="Preview"
                  className="w-full h-full object-contain rounded-md"
                />
              </div>
            </div>

            <div>
              <Label className="pb-2">Status</Label>
              <div className="mt-1">
                <Badge variant={product.inactive ? "secondary" : "default"}>
                  {product.inactive ? "Inactive" : "Active"}
                </Badge>
              </div>
            </div>
            <div>
              <Label className="pb-2">Operation Type</Label>
              <div className="mt-1">{getBuyOrSellBadge(product.buyOrSell)}</div>
            </div>
          </div>

          {/* Label Overrides */}
          {(product.usernameLabelOverride || product.emailLabelOverride || product.passwordLabelOverride) && (
            <>
              <Separator />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="pb-2">Username Label Override</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {product.usernameLabelOverride || <span className="italic">Not set</span>}
                  </p>
                </div>
                <div>
                  <Label className="pb-2">Email Label Override</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {product.emailLabelOverride || <span className="italic">Not set</span>}
                  </p>
                </div>
                <div>
                  <Label className="pb-2">Password Label Override</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {product.passwordLabelOverride || <span className="italic">Not set</span>}
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Sell Configuration */}
          {(product.buyOrSell === ProductType.SELL ||
            product.buyOrSell === ProductType.BOTH) && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="pb-2">Minimum Value for Sell</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {product.minimumValueForSell?.toLocaleString() || "Not set"}
                  </p>
                </div>
                <div>
                  <Label className="pb-2">Maximum Value for Sell</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {product.maximumValueForSell?.toLocaleString() || "Not set"}
                  </p>
                </div>
              </div>
              {product.sellPrices && (
                <div className="space-y-3">
                  <Label>Product Prices For Sell</Label>
                  <div className="overflow-x-auto">
                    <div className="mt-2 space-y-2">
                      {product.sellPrices.map((tier, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center p-3 bg-muted rounded-lg"
                        >
                          <span className="text-sm">
                            {tier.minRange.toLocaleString()} -{" "}
                            {tier.maxRange.toLocaleString()} miles
                          </span>
                          <span className="text-sm font-medium">
                            ${tier.price} per mile
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Buy Configuration */}
          {(product.buyOrSell === ProductType.BUY ||
            product.buyOrSell === ProductType.BOTH) && (
            <>
              <Separator />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="pb-2">Minimum Value for Buy</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {product.minimumValueForBuy?.toLocaleString() || "Not set"}
                  </p>
                </div>
                <div>
                  <Label className="pb-2">Maximum Value for Buy</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {product.maximumValueForBuy?.toLocaleString() || "Not set"}
                  </p>
                </div>
              </div>
              {product.buyPrices && (
                <div className="space-y-3">
                  <Label>Product Prices For Buy</Label>
                  <div className="overflow-x-auto">
                    <div className="mt-2 space-y-2">
                      {product.buyPrices.map((tier, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center p-3 bg-muted rounded-lg"
                        >
                          <span className="text-sm">
                            {tier.minRange.toLocaleString()} -{" "}
                            {tier.maxRange.toLocaleString()} miles
                          </span>
                          <span className="text-sm font-medium">
                            ${tier.price} per mile
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Status Tiers */}
          {product.statusTiers && product.statusTiers.length > 0 && (
            <>
              <Separator />

              <div className="space-y-3">
                <Label>Status Tiers</Label>
                <div className="overflow-x-auto">
                  <div className="mt-2 space-y-2">
                    {product.statusTiers.map((tier, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center p-3 bg-muted rounded-lg"
                      >
                        <span className="text-sm font-medium">{tier.name}</span>
                        <span className="text-sm">{tier.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Metadata */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t">
            <div>
              <Label className="pb-2">Created At</Label>
              <p className="text-sm text-muted-foreground mt-1">
                {new Date(product.createdAt).toLocaleString()}
              </p>
            </div>
            <div>
              <Label className="pb-2">Last Updated</Label>
              <p className="text-sm text-muted-foreground mt-1">
                {new Date(product.updatedAt).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
